import React, { useState, useRef, useEffect, useCallback } from 'react';
import { HexColorPicker } from 'react-colorful';
import { createPortal } from 'react-dom';
import { KeenIcon } from '@/components';

const getHexColor = (val) => '#' + val.padEnd(6, '0');

const ColorInput = ({ label, value, onChange }) => {
    const [inputValue, setInputValue] = useState(value.replace('#', ''));
    const [showPicker, setShowPicker] = useState(false);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
    const pickerRef = useRef();
    const buttonRef = useRef();
    const isDraggingRef = useRef(false);
    const lastColorRef = useRef('');

    // ✅ Đồng bộ khi prop value thay đổi từ cha
    useEffect(() => {
        let hex = value.replace('#', '');
        // Nếu là mã màu hợp lệ (6 ký tự, không phải toàn số 0), giữ nguyên
        if (/^#[0-9a-fA-F]{6}$/.test(value) && hex !== '000000' && hex !== 'ffffff' && hex !== 'FFFFFF') {
            hex = hex.replace(/0+$/, '');
        }
        setInputValue(hex);
    }, [value]);

    const handleColorChange = useCallback((color) => {
        // Chỉ cập nhật nếu màu thực sự thay đổi
        if (color !== lastColorRef.current) {
            lastColorRef.current = color;
            const cleanColor = color.replace('#', '');
            setInputValue(cleanColor);
            // Không gọi onChange ngay, chỉ cập nhật UI
            // onChange(getHexColor(cleanColor));
        }
    }, []);

    const handleTextChange = (e) => {
        const val = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
        setInputValue(val);
        // Luôn truyền lên cha, luôn fill màu (dù chưa đủ 6 ký tự)
        onChange(getHexColor(val));
    };

    const handleShowPicker = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const pickerWidth = 200; // Ước tính chiều rộng picker
            const pickerHeight = 200; // Ước tính chiều cao picker
            
            let top = rect.bottom + window.scrollY + 8;
            let left = rect.left + window.scrollX;
            
            // Kiểm tra và điều chỉnh vị trí nếu bị che bởi cạnh màn hình
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            // Kiểm tra cạnh phải
            if (left + pickerWidth > viewportWidth) {
                left = rect.right + window.scrollX - pickerWidth - 8;
            }
            
            // Kiểm tra cạnh trái
            if (left < 0) {
                left = 8;
            }
            
            // Kiểm tra cạnh dưới
            if (top + pickerHeight > viewportHeight + window.scrollY) {
                // Hiển thị phía trên button thay vì phía dưới
                top = rect.top + window.scrollY - pickerHeight - 8;
            }
            
            // Kiểm tra cạnh trên
            if (top < window.scrollY) {
                top = window.scrollY + 8;
            }
            
            setPickerPosition({ top, left });
        }
        setShowPicker(true);
    };

    const handleMouseDown = useCallback(() => {
        isDraggingRef.current = true;
    }, []);

    const handleMouseUp = useCallback(() => {
        // Gọi onChange khi kết thúc sự kiện mouse
        if (lastColorRef.current) {
            const cleanColor = lastColorRef.current.replace('#', '');
            onChange(getHexColor(cleanColor));
        }
        // Delay reset để tránh nháy
        setTimeout(() => {
            isDraggingRef.current = false;
        }, 100);
    }, [onChange]);

    useEffect(() => {
        if (!showPicker) return;
        
        const handleClickOutside = (event) => {
            // Chỉ đóng picker nếu không đang kéo
            if (!isDraggingRef.current) {
                if (
                    pickerRef.current &&
                    !pickerRef.current.contains(event.target) &&
                    buttonRef.current &&
                    !buttonRef.current.contains(event.target)
                ) {
                    // Gọi onChange khi đóng picker
                    if (lastColorRef.current) {
                        const cleanColor = lastColorRef.current.replace('#', '');
                        onChange(getHexColor(cleanColor));
                    }
                    setShowPicker(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('mouseup', handleMouseUp);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [showPicker, handleMouseUp]);

    return (
        <div className="relative">
            <label className="block mb-1">{label}</label>
            <div className="flex items-center gap-2">
                <div
                    ref={buttonRef}
                    className="inline-flex items-center px-4 py-2 border border-[#F0EFED] rounded-[4px] space-x-2 cursor-pointer relative"
                    onClick={handleShowPicker}
                >
                    <div
                        className="w-6 h-5 rounded-[4px] border border-gray-400"
                        style={{ backgroundColor: getHexColor(inputValue) }}
                    />
                    <input
                        type="text"
                        maxLength={6}
                        value={inputValue}
                        onChange={handleTextChange}
                        className="font-mono text-sm text-gray-700 bg-transparent border-none outline-none w-full"
                        style={{ textTransform: 'uppercase' }}
                    />
                </div>
            </div>
            {showPicker && createPortal(
                <div 
                    ref={pickerRef} 
                    className="fixed z-[9999] rounded-lg shadow-lg"
                    style={{
                        top: pickerPosition.top,
                        left: pickerPosition.left
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseUp={handleMouseUp}
                >
                    <HexColorPicker
                        color={getHexColor(inputValue)}
                        onChange={handleColorChange}
                    />
                </div>,
                document.body
            )}
        </div>
    );
};

export { ColorInput };
